// Module 10 — flow-glyphs.ts: the marching ARROW-GLYPH renderer (engine-v2 flow redesign; the user's
// "large, recognizable moving chevron arrows, portion by portion"). Replaces the sub-visible
// chevron-on-thin-polyline material with discrete arrow SPRITES that physically travel along each
// energized edge. One shared Cesium BillboardCollection holds a fixed pool of billboards; per snapshot
// we assign pool slots to the energized+flowing edges (spaced along each edge); each rAF frame we march
// every slot a little further along its path and re-point it. Orientation rides Cesium's `alignedAxis`
// (the world-space segment direction) so arrows stay aligned as the camera moves; `color` tints the one
// white arrow texture per voltage tier; pixel sizing keeps them big at any zoom.
//
// The geometry core (buildTrack/sampleTrack) is PURE and unit-tested; only the Cesium binding (texture
// generation, billboard pool) needs the browser. Engine-driven: a slot exists ONLY where the engine
// marked the edge energized with flowDir≠0 — de-energized/idle edges get no glyphs (the line itself is
// still drawn by the adapter's static polyline). Direction = the engine's flowDir; prosumer export and
// FLISR re-routing flip it for free.
import type { CesiumLike, BillboardCollectionLike, BillboardLike, Cartesian3Like } from "./cesium-api";

// ── pure geometry core ────────────────────────────────────────────────────────────────────────
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}
export interface Track {
  pts: Vec3[]; // ECEF (or any cartesian) vertices
  cum: number[]; // cumulative arc length at each vertex (cum[0]=0)
  length: number; // total path length
}

export function buildTrack(pts: readonly Vec3[]): Track {
  const cum: number[] = [0];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!;
    const b = pts[i]!;
    cum.push(cum[i - 1]! + Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z));
  }
  return { pts: pts as Vec3[], cum, length: cum[cum.length - 1] ?? 0 };
}

// Position + unit direction at arc-distance `dist` (clamped to [0,length]). Null for a zero-length
// track (a degenerate single-point edge — nothing to march along).
export function sampleTrack(t: Track, dist: number): { pos: Vec3; dir: Vec3 } | null {
  if (t.length <= 0 || t.pts.length < 2) return null;
  const d = dist < 0 ? 0 : dist > t.length ? t.length : dist;
  // find segment i with cum[i] <= d <= cum[i+1]
  let i = 0;
  while (i < t.cum.length - 2 && t.cum[i + 1]! < d) i++;
  const a = t.pts[i]!;
  const b = t.pts[i + 1]!;
  const segLen = t.cum[i + 1]! - t.cum[i]!;
  const f = segLen > 0 ? (d - t.cum[i]!) / segLen : 0;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;
  const m = Math.hypot(dx, dy, dz) || 1;
  return {
    pos: { x: a.x + dx * f, y: a.y + dy * f, z: a.z + dz * f },
    dir: { x: dx / m, y: dy / m, z: dz / m },
  };
}

// ── Cesium billboard layer ──────────────────────────────────────────────────────────────────────
export interface GlyphEdge {
  id: string;
  polyline: readonly (readonly [number, number])[]; // [lon,lat] vertices (true OSM geometry)
  heightM?: number; // FLAT elevation (0 for ground energy edges)
  heights?: readonly number[]; // OPTIONAL per-vertex elevation (ballistic info/finance arcs) — when
  // present, overrides heightM so the marching arrows ride the SAME arch the arc line draws.
}
export interface GlyphActive {
  colorCss: string; // tier hue (energy) or layer hue (info/finance)
  flowDir: 1 | -1; // engine sign(flow) — the marching direction
  flowMag: number; // 0..1 (|flow|/rateMva) → march speed
  mw: number; // ABSOLUTE |flowMw| → arrow SIZE (twice the energy ⇒ ~twice the arrow, per user)
  halo?: "hot" | "red"; // P14: a danger HALO behind the arrow when the edge is overloaded (hot ≥85, red ≥100)
}
export interface FlowGlyphOpts {
  maxGlyphs?: number; // pool cap (default 4000)
  spacingM?: number; // arc-distance between glyphs on one edge (default 3500 m)
  perEdgeCap?: number; // max glyphs on a single edge (default 10)
  pxSize?: number; // MAX arrow sprite size in pixels (the size of a fully-loaded edge)
  minPx?: number; // smallest visible arrow (a barely-flowing edge)
  refMw?: number; // |flowMw| that maps to pxSize (edges at/above this draw at full size)
  baseSpeedMps?: number; // march speed at flowMag 0 (default 1800 m/s of model space ≈ lively)
  badge?: string; // glyph baked behind the arrowhead ("$" finance · "i" information · none = energy)
  halo?: boolean; // P14: build a second danger-HALO billboard pool (energy edges only); see GlyphActive.halo
}
export interface FlowGlyphLayer {
  readonly collection: BillboardCollectionLike;
  // P14: the danger-halo pool (a soft disc UNDER each arrow on a hot/red edge), null unless opts.halo.
  // The adapter adds it to the scene below `collection` and lifts/disposes it alongside.
  readonly haloCollection: BillboardCollectionLike | null;
  setEdges(edges: readonly GlyphEdge[]): void; // precompute tracks (once at mount for energy edges)
  setActive(active: ReadonlyMap<string, GlyphActive>): void; // per snapshot: which edges show glyphs
  // Phase 0.2: live tier knob — bounds how many glyph slots setActive may use per snapshot. The pool
  // still grows up to the construction `maxGlyphs` (the ceiling across all tiers), but the live cap
  // drops with quality so a weaker device renders fewer arrows. Driven by tierKnobs(tier).glyphCap.
  setGlyphCap(n: number): void;
  // Phase 0.3: live tier knob — scales the march speed by 1/cadence (cadence 1 = full speed, 4 = quarter
  // speed). The low tier slows the arrow train so fewer intermediate positions need rendering per second.
  setCadence(cadence: number): void;
  advance(elapsedSec: number): void; // per frame: march + re-point every active glyph
  isAnimating(): boolean; // P16: are any glyph slots active this snapshot? (the rAF render-gate reads this)
  hideAll(): void;
  dispose(): void;
}

interface Slot {
  bb: BillboardLike;
  haloBb: BillboardLike | null; // P14: companion danger-halo billboard (only when opts.halo); null otherwise
  scratchPos: Cartesian3Like; // reused so per-frame repositioning never allocates
  scratchAxis: Cartesian3Like;
  trackId: string;
  baseOffset: number; // start distance along the track
  flowDir: 1 | -1;
  speed: number; // m/s along the track
  haloShown: boolean; // is this slot's halo active this snapshot (so advance() re-points it)
}

// P14 danger-halo hue, matched to the edge loading ramp (hot orange / red vermillion).
const HALO_COLOR: Record<"hot" | "red", string> = { hot: "#E69F00", red: "#D55E00" };

// PURE arrow-size law (exported for unit test): |flowMw| → pixel size via a PERCEPTUAL sqrt map clamped
// to [minPx, pxSize] referenced at refMw (~p98 of real |flowMw|), so a 3200 MW backbone corridor reads
// visibly bigger than a tens-of-MW feeder without the backbone saturating to one size (the reality-audit
// "arrow size ≠ energy" defect). Math: minPx + (pxSize−minPx)·min(1, √(|mw|/refMw)).
export function glyphPxForMw(mw: number, opts: { minPx: number; pxSize: number; refMw: number }): number {
  const a = Number.isFinite(mw) ? Math.abs(mw) : 0;
  return opts.minPx + (opts.pxSize - opts.minPx) * Math.min(1, Math.sqrt(a / opts.refMw));
}

// A bold upward-pointing chevron arrow on transparent — white so billboard.color can tint it per tier.
// Drawn pointing toward image-TOP (+y up) so alignedAxis(segment dir) makes it point along the edge.
// `badge` (e.g. "$" for finance, "i" for information) is baked BEHIND the arrowhead so the three parallel
// flow layers read apart by SHAPE as well as colour (energy = plain double chevron, no badge).
function arrowDataUrl(px: number, badge?: string): string {
  if (typeof document === "undefined") return ""; // node unit tests have no DOM — skip texture gen
  const c = document.createElement("canvas");
  c.width = px;
  c.height = px;
  const ctx = c.getContext("2d")!;
  ctx.clearRect(0, 0, px, px);
  ctx.strokeStyle = "#ffffff";
  ctx.fillStyle = "#ffffff";
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  const m = px * 0.16; // margin
  if (badge) {
    // single arrowhead in the TOP ~40%, the badge glyph attached BEHIND it (bottom ~50%): reads ">$"/">i".
    ctx.lineWidth = Math.max(2, px * 0.12);
    ctx.beginPath();
    ctx.moveTo(m, px * 0.40);
    ctx.lineTo(px * 0.5, px * 0.05);
    ctx.lineTo(px - m, px * 0.40);
    ctx.stroke();
    ctx.font = `bold ${Math.round(px * 0.52)}px system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(badge, px * 0.5, px * 0.74);
  } else {
    // plain "∧∧" double chevron (energy flows)
    ctx.lineWidth = Math.max(2, px * 0.16);
    ctx.beginPath();
    ctx.moveTo(m, px * 0.62);
    ctx.lineTo(px * 0.5, px * 0.18);
    ctx.lineTo(px - m, px * 0.62);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(m, px - m);
    ctx.lineTo(px * 0.5, px * 0.42);
    ctx.lineTo(px - m, px - m);
    ctx.stroke();
  }
  return c.toDataURL();
}

// P14 danger HALO sprite: a soft white radial disc (rotation-invariant, so it needs no alignedAxis),
// tinted to the danger hue + drawn LARGER & UNDER the arrow → a glowing ring around the marching chevron
// on an overloaded edge. White so billboard.color tints it; node tests with no DOM skip texture gen.
function discDataUrl(px: number): string {
  if (typeof document === "undefined") return "";
  const c = document.createElement("canvas");
  c.width = px;
  c.height = px;
  const ctx = c.getContext("2d")!;
  ctx.clearRect(0, 0, px, px);
  const r = px * 0.5;
  const g = ctx.createRadialGradient(r, r, px * 0.10, r, r, r); // bright core → transparent rim
  g.addColorStop(0, "rgba(255,255,255,0.95)");
  g.addColorStop(0.55, "rgba(255,255,255,0.45)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(r, r, r, 0, Math.PI * 2);
  ctx.fill();
  return c.toDataURL();
}

export function makeFlowGlyphLayer(cesium: CesiumLike, opts: FlowGlyphOpts = {}): FlowGlyphLayer {
  const maxGlyphs = opts.maxGlyphs ?? 4000;
  const spacingM = opts.spacingM ?? 3500;
  const perEdgeCap = opts.perEdgeCap ?? 10;
  const pxSize = opts.pxSize ?? 16; // halved per user ("reduce size by 50% in general")
  const minPx = opts.minPx ?? 4;
  const refMw = opts.refMw ?? 800; // |flowMw| that draws at full pxSize (≈ p98 of real |flowMw|)
  const baseSpeed = opts.baseSpeedMps ?? 1800;

  // Arrow SIZE grows with energy on the edge (user: "size must correspond to the amount of energy").
  // The perceptual sqrt law (exported `glyphPxForMw`) spreads the ~3000:1 dynamic range so bulk
  // transmission reads visibly bigger than distribution while small feeders stay legible.
  const sizeForMw = (mw: number): number => glyphPxForMw(mw, { minPx, pxSize, refMw });

  const collection = new cesium.BillboardCollection();
  const image = arrowDataUrl(64, opts.badge); // one white arrow(+badge) texture, tinted per glyph via .color
  // P14: a SECOND pool of danger-halo discs UNDER the arrows (energy layer only — info/finance have no
  // loading band). Null when opts.halo is off so info/finance pay nothing for it.
  const haloCollection: BillboardCollectionLike | null = opts.halo ? new cesium.BillboardCollection() : null;
  const haloImage = opts.halo ? discDataUrl(64) : "";
  const tracks = new Map<string, Track>();
  const colorCache = new Map<string, unknown>();
  const colorOf = (css: string): unknown => {
    let c = colorCache.get(css);
    if (c === undefined) { c = cesium.Color.fromCssColorString(css); colorCache.set(css, c); }
    return c;
  };

  const slots: Slot[] = [];
  let activeCount = 0; // first `activeCount` slots are in use this snapshot
  let lastSec = 0; // last march clock — so setActive can place new glyphs even before the next rAF frame
  // Phase 0.2: the live glyph cap. Initialized to the pool ceiling; applyQuality lowers it per tier.
  // setActive reads THIS (not maxGlyphs) so the governor's tier drop immediately renders fewer arrows
  // without rebuilding the layer. The pool (slots.length) can still grow up to maxGlyphs.
  let glyphCap = maxGlyphs;
  // Phase 0.3: the march-speed multiplier (1/cadence). cadence=1 ⇒ full speed (default); cadence=4 ⇒
  // the low-tier "every 4th frame" stride. advance() scales the per-slot offset delta by this.
  let cadenceMult = 1;

  // Position + orient one active slot at march clock `sec` (no allocation — mutates reused scratch).
  const placeSlot = (s: Slot): void => {
    const t = tracks.get(s.trackId);
    if (t === undefined || t.length <= 0) { s.bb.show = false; if (s.haloBb) s.haloBb.show = false; return; }
    let d = (s.baseOffset + s.flowDir * s.speed * lastSec * cadenceMult) % t.length;
    if (d < 0) d += t.length;
    const sample = sampleTrack(t, d);
    if (sample === null) { s.bb.show = false; if (s.haloBb) s.haloBb.show = false; return; }
    s.scratchPos.x = sample.pos.x; s.scratchPos.y = sample.pos.y; s.scratchPos.z = sample.pos.z;
    const sgn = s.flowDir;
    s.scratchAxis.x = sample.dir.x * sgn; s.scratchAxis.y = sample.dir.y * sgn; s.scratchAxis.z = sample.dir.z * sgn;
    s.bb.position = s.scratchPos; // Cesium copies the values (no per-frame allocation)
    s.bb.alignedAxis = s.scratchAxis;
    if (s.haloBb && s.haloShown) s.haloBb.position = s.scratchPos; // the danger halo rides the arrow
  };

  const ensure = (n: number): void => {
    while (slots.length < n && slots.length < maxGlyphs) {
      const bb = collection.add({
        position: new cesium.Cartesian3(0, 0, 0), image, show: false,
        color: colorOf("#ffffff"), sizeInMeters: false, width: pxSize, height: pxSize,
        id: { kind: "glyph" },
      });
      const haloBb = haloCollection
        ? haloCollection.add({
            position: new cesium.Cartesian3(0, 0, 0), image: haloImage, show: false,
            color: colorOf("#ffffff"), sizeInMeters: false, width: pxSize, height: pxSize,
            id: { kind: "glyph-halo" },
          })
        : null;
      slots.push({
        bb, haloBb, scratchPos: new cesium.Cartesian3(0, 0, 0), scratchAxis: new cesium.Cartesian3(0, 0, 0),
        trackId: "", baseOffset: 0, flowDir: 1, speed: baseSpeed, haloShown: false,
      });
    }
  };

  return {
    collection,
    haloCollection,

    setEdges(edges): void {
      tracks.clear();
      for (const e of edges) {
        if (e.polyline.length < 2) continue;
        const flat = e.heightM ?? 0;
        const pts: Vec3[] = e.polyline.map(([lon, lat], i) => {
          const h = e.heights ? (e.heights[i] ?? flat) : flat; // per-vertex arch height when provided
          const c = cesium.Cartesian3.fromDegrees(lon, lat, h);
          return { x: c.x, y: c.y, z: c.z };
        });
        const t = buildTrack(pts);
        if (t.length > 0) tracks.set(e.id, t);
      }
    },

    setActive(active): void {
      // Assign glyph slots to active edges, biggest-flow first so the cap favours the busy corridors.
      const ids = [...active.keys()]
        .filter((id) => tracks.has(id))
        .sort((a, b) => (active.get(b)!.flowMag - active.get(a)!.flowMag) || a.localeCompare(b));
      let slot = 0;
      for (const id of ids) {
        if (slot >= glyphCap) break;
        const t = tracks.get(id)!;
        const a = active.get(id)!;
        // ≥3 evenly-spaced glyphs per edge so the marching train always has an arrow on-track: with a
        // single glyph, the modulo wrap (placeSlot) snaps it from edge-end back to the start = a visible
        // JUMP/discontinuity on short edges (most distribution + many transmission are < spacingM). Three
        // staggered glyphs mean one is always mid-track while another wraps ⇒ a continuous, steady flow.
        const n = Math.min(perEdgeCap, Math.max(3, Math.floor(t.length / spacingM)));
        const step = t.length / n;
        const speed = baseSpeed * (0.5 + Math.min(1, a.flowMag)); // busier edges march faster
        const col = colorOf(a.colorCss);
        // CAPACITY-PROPORTIONAL arrow size: combine absolute MW (so a 3000 MW backbone reads bigger than
        // a 30 MW feeder) with loading fraction flowMag (so a 60%-loaded feeder gets bigger arrows than
        // a 5%-loaded backbone, even if absolute MW is similar). The flowMag term (|flow|/rateMva) is the
        // capacity-normalized signal the user asked for — arrows now reflect how STRESSED each edge is
        // relative to its own capacity, not just raw MW.
        const pxMw = sizeForMw(a.mw); // absolute MW → base size
        const pxLoad = minPx + (pxSize - minPx) * Math.min(1, a.flowMag); // loading fraction → pressure size
        const px = Math.round((pxMw * 0.6 + pxLoad * 0.4) * 4) / 4; // blend: 60% absolute MW, 40% loading fraction
        for (let k = 0; k < n && slot < glyphCap; k++) {
          ensure(slot + 1);
          if (slot >= slots.length) break;
          const s = slots[slot]!;
          s.trackId = id;
          // carry the packet id so a click on a moving arrow resolves to its from→to datasheet (picking.ts).
          // For energy glyphs `id` is a real edge id (also selectable); for info/finance it's a from→to id.
          (s.bb as { id?: unknown }).id = { kind: "glyph", id };
          s.baseOffset = k * step;
          s.flowDir = a.flowDir;
          s.speed = speed;
          s.bb.color = col;
          s.bb.width = px;
          s.bb.height = px;
          s.bb.show = true;
          // P14 danger halo: a soft danger-tinted disc UNDER the arrow when the edge is hot/red.
          if (s.haloBb) {
            if (a.halo) {
              const hp = px * 1.8; // larger than the arrow → a glowing ring around it
              s.haloBb.color = colorOf(HALO_COLOR[a.halo]);
              s.haloBb.width = hp;
              s.haloBb.height = hp;
              s.haloBb.show = true;
              s.haloShown = true;
            } else {
              s.haloBb.show = false;
              s.haloShown = false;
            }
          }
          slot++;
        }
      }
      for (let i = slot; i < slots.length; i++) {
        slots[i]!.bb.show = false;
        if (slots[i]!.haloBb) { slots[i]!.haloBb!.show = false; slots[i]!.haloShown = false; }
      }
      activeCount = slot;
      // place immediately so glyphs appear on the FIRST rendered frame (and stay correct in a
      // backgrounded tab where rAF is paused) — not only once the rAF march loop starts.
      for (let i = 0; i < activeCount; i++) placeSlot(slots[i]!);
    },

    advance(elapsedSec): void {
      lastSec = elapsedSec;
      for (let i = 0; i < activeCount; i++) placeSlot(slots[i]!);
    },

    // Phase 0.2: live tier knob — clamps the active-slot budget without rebuilding the layer.
    setGlyphCap(n: number): void {
      // Clamp to [0, maxGlyphs] — the pool ceiling is fixed at construction; the live cap may be lower
      // (low tier) but never higher (would over-allocate slots the pool can't hold).
      glyphCap = Math.max(0, Math.min(maxGlyphs, Math.floor(n)));
    },

    // Phase 0.3: live tier knob — scales the march speed by 1/cadence. cadence=1 (high tiers) = full
    // speed; cadence=4 (low tier) = quarter speed (a slower, calmer arrow train that needs fewer
    // intermediate position writes to read smoothly).
    setCadence(cadence: number): void {
      cadenceMult = cadence > 0 ? 1 / cadence : 1;
    },

    isAnimating(): boolean { return activeCount > 0; },

    hideAll(): void {
      for (const s of slots) { s.bb.show = false; if (s.haloBb) { s.haloBb.show = false; s.haloShown = false; } }
      activeCount = 0;
    },

    dispose(): void {
      collection.removeAll();
      haloCollection?.removeAll();
      slots.length = 0;
      tracks.clear();
      colorCache.clear();
    },
  };
}
